/*
 * File: c:\Users\j.mazat\Desktop\AutobuyBackOffice\AutobuyBackOffice\src\Services\Car\CarService.ts
 * Project: c:\Users\j.mazat\Desktop\AutobuyBackOffice\AutobuyBackOffice
 * Created Date: Thursday June 8th 2023
 * Author: Jonathan Mazat
 * -----
 * Last Modified: Monday, 23rd September 2024 03:09:50
 * Modified By: Simon (s.gourlet@autobuy.fr>)
 * -----
 * Copyright (c) 2023 Autobuy SAS
 * -----
 */

import Config from "Config/Config";
import { SingleCarFormat } from "Type/CarElement.type.hold";
import generalQueryParams from "Type/params.type";
import { buildAxiosHeader, buildAxiosHeaderFile, buildURL } from "Utils/Request.utils";
import Axios from "axios";

export default class CarService {
    static async transformSingleCar(car: any): Promise<SingleCarFormat | null> {
        if (car === undefined || car === null)
            return null;
        const newCar = new SingleCarFormat();
        newCar.fromJson(car);
        return newCar;
    }

    static async getCarByID(id: string) {
        const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.CARS}/${id}`;
        const myCar = await Axios.get(url, buildAxiosHeader());
        return myCar.data;
    }

    static async getCarByIDForSearch(id: string) {
        const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.CARS}/${id}/for_search`;
        const myCar = await Axios.get(url, buildAxiosHeader());
        return myCar.data;
    }

    static async getCarOffersByID(id: string) {
        const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.CARS}/${id}/offers`;
        const myCarOffers = await Axios.get(url, buildAxiosHeader());
        return (myCarOffers.data.InSell as any[]).map(v => v.Offers).flat(2);
    }

    static async getCarOldOffersByID(id: string) {
        const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.CARS}/${id}/offers`;
        const myCarOffers = await Axios.get(url, buildAxiosHeader());
        return myCarOffers.data.InSell;
    }

    static async getSendCarIntelToClient(carID: string, userID: string) {
        const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.CARS}/${carID}/send_intels`;
        const myRet = await Axios.post(url, { userID }, buildAxiosHeader());
        return myRet;
    }

    // static async setCarByID(id: string, car: SingleCarFormat | UpdateCarInfo) {
    static async setCarByID(id: string, car: any) {
        const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.CARS}/${id}`;
        const body = car;
        const headers = buildAxiosHeader();
        try {
            return await Axios.put(url, body, headers);
        } catch (e) {
            console.error(`Not able to update car: ${JSON.stringify(e)}`);
        }
        return null;
    }

    static async setCarPicturesByID(id: string, pictures_update: any) {
        const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.CARS}/${id}/car_pictures`;
        const body = pictures_update;
        try {
            return await Axios.put(url, body, buildAxiosHeader());
        } catch (e) {
            console.error(`Not able to update car pictures: ${JSON.stringify(e)}`);
        }
        return null;
    }

    static async deleteCarFromSell(sellId: string, carId: string) {
        const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.SELLS}/${sellId}/${carId}`;
        const headers = buildAxiosHeader();
        try {
            return await Axios.put(url, {}, headers);
        } catch (e) {
            console.error(`Not able to update car: ${JSON.stringify(e)}`);
            throw e;
        }
    }

    static createCarPicture(id: string, name: string, index: number, picture: File) {
        const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.CARS}/${id}/add_picture`;
        var body = { picture, name, index };
        return Axios.put(url, body, buildAxiosHeaderFile());
    }

    static async getCarsByRegNb(regNb: string) {
        const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.CARS}/regnb/${regNb}`;
        const headers = buildAxiosHeader();
        try {
            const cars = await Axios.get<any[]>(url, headers);
            return cars.data;
        } catch (e) {
            console.error(`Not able to pull cars with regNb containing ${regNb}: ${JSON.stringify(e)}`);
        }
        return [];
    }

    static async makeOfferForCar(carID: string, offerValue: number, myID: string, userID: string, directAssign: boolean = false) {
        const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.OFFER}`;
        const headers = buildAxiosHeader();
        if (userID === "") {
            console.error(`Not able to make an offer for car ${carID}: invalid user id`);
            return [];
        }
        try {
            const cars = await Axios.post(url, { carID, commercialID: myID, userID, value: offerValue, gifted: directAssign }, headers);
            return cars.data;
        } catch (e) {
            console.error(`Not able to make an offer for car ${carID}: ${JSON.stringify(e)}`);
        }
        return [];
    }

    static async retrievePicturesForCar(carID: string) {
        const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.CARS}/${carID}/retrieve_pictures`;
        const headers = buildAxiosHeader();
        try {
            const cars = await Axios.get(url, headers);
            return cars.data;
        } catch (e: any) {
            console.error(`Not able to retrieve pictures for car ${carID}: ${JSON.stringify(e.message)}`);
            throw e;
        }
    }

    static async getCarBySearch(search: string, tabkey: string) {
        const params: generalQueryParams = { status: tabkey, perpage: 9999 };
        const builtUrl = buildURL(params, `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.CARS}/search/${search}`);
        try {
            const myRet = await Axios.get(builtUrl, buildAxiosHeader());
            if (myRet.data)
                return myRet.data.map((v: any) => { return { label: `${v.RegNb} - ${v.Brand} - ${v.Model}`.toUpperCase(), result: v } });
            else
                return [];
        } catch (error) {
            return [];
        }
    }

    static async getCarInfo() {
        const myUrl = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.CARSINFO}`;
        const carInfo = await Axios.get(myUrl, buildAxiosHeader());
        return carInfo.data;
    }

    static async addNewCars(file: File, archive: boolean, cb?: (line: string) => void) {
        const url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.CARSYNCHRO}`;
        const formData = new FormData();
        formData.append("cars", file);
        if (archive)
            formData.append("archive", "true");

        const response = await fetch(url, {
            method: "POST",
            headers: { 'Authorization': buildAxiosHeaderFile().headers['Authorization'] },
            body: formData,
        });

        if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText);
        }

        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        if (!reader) throw new Error("Pas de flux lisible");

        let buffer = "";

        while (true) {
            const { done, value } = await reader.read();

            buffer += decoder.decode(value, { stream: true });

            let lines = buffer.split("\n");
            buffer = lines.pop() || "";

            if (done)
                return buffer;

            for (const line of lines) {
                if (cb && line.trim()) cb(line);
            }
        }
    }

    static async getCarList(params: generalQueryParams) {
        try {
            const builtUrl = buildURL(params, `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.CARS}`);
            const carList = await Axios.get(builtUrl, buildAxiosHeader());
            return carList.data;
        } catch (error) {
            throw error;
        }
    }

    static async getCarPictures(id: string, index?: number) {
        try {
            let url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.GET_CAR_PICTURE}/${id}`;
            if (typeof index === "number")
                url = `${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.GET_CAR_PICTURE}/${id}/${index}`;
            const carPicture = await Axios.get(url, buildAxiosHeader());
            return carPicture.data;
        } catch (error) {
            return [];
        }
    }

    static async getCarEquipements(id: string) {
        try {
            const carEquipment = await Axios.get(`${Config.BACKEND.BASEURL}${Config.BACKEND.PATHS.GET_CAR_EQUIPMENT}/${id}`, buildAxiosHeader());
            return carEquipment.data;
        } catch (error) {
            return [];
        }
    }
}
